import type { Signal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { ScrollArea } from '@workspace/ui/components/scroll-area';
import { useEffect, useRef } from 'react';

import { ThinkingMessage } from '~/components/thinking-message';
import type {
  Message,
  MessageStatus,
  ThinkingMessageItem,
  ToolCall,
} from '~/hooks/use-chat/types';

import { ChatMessage } from './message';

type ChatMessagesProps = {
  thinkings: Signal<ThinkingMessageItem[]>;
  messages: Signal<Message[]>;
  status: Signal<MessageStatus>;
  handleAction: (action: ToolCall, response: string) => Promise<void>;
};

export function ChatMessages({
  thinkings,
  status,
  messages,
  handleAction,
}: ChatMessagesProps) {
  useSignals();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const thinkingEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messages.value.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.value]);

  useEffect(() => {
    if (thinkings.value.length > 0 && thinkingEndRef.current) {
      thinkingEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thinkings.value]);

  return (
    <ScrollArea className="flex-1 overflow-y-auto p-0">
      <div className="mx-auto space-y-4 pb-4 min-w-0">
        {messages.value.map((msg, index) => {
          const isLast = index === messages.value.length - 1;
          return (
            <ChatMessage
              key={msg.id}
              message={msg}
              handleAction={handleAction}
              isActive={isLast}
            />
          );
        })}
        {status.value === 'submitted' && thinkings.value.length > 0 && (
          <div className="pt-6">
            <ThinkingMessage
              key={`thinking-${thinkings.value.length - 1}`}
              thinking={thinkings.value[thinkings.value.length - 1]?.content}
            />
            <div ref={thinkingEndRef} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}
